import {
  DefectCatalogItem,
  toDefectMeasurementSlug,
  toDefectSlug,
  toDefectSurveyYearSlug,
} from '../contracts/availableLayers';
import {
  CRACKMAP_INITIAL_ZOOM,
  CRACKMAP_WMTS_FORMAT,
  CRACKMAP_WMTS_SERVICE_URL,
  CRACKMAP_WMTS_TILE_MATRIX_SET,
} from '../crackmaps/crackmapCatalog';
import type { DefectOverviewGridConfig } from '../defectOverlays/overviewGrid';
import {
  POTHOLE_RAG_COLOR_SCHEME,
  POTHOLE_RAG_THRESHOLDS,
} from './potholeBands';

export const WINDSOR_POTHOLE_TYPE_NAME = 'wfs_layers:windsor-mfv-potholes-2026-27';

export const WINDSOR_POTHOLE_WFS_URL =
  `https://geoserver.xais.co.uk/geoserver/wfs_layers/ows?service=WFS&version=1.0.0&request=GetFeature&typeName=${encodeURIComponent(WINDSOR_POTHOLE_TYPE_NAME)}`;

export const POTHOLE_WFS_PROPERTY_NAMES =
  'geometry,sectionID,potHoleID,maxDepth,aveDepth,area,severity,majorDia,minorDia';

export const POTHOLE_OVERVIEW_WMTS_STYLE = 'pothole';

export const WINDSOR_POTHOLE_OVERVIEW_WMTS: DefectOverviewGridConfig = {
  serviceUrl: CRACKMAP_WMTS_SERVICE_URL,
  tileMatrixSet: CRACKMAP_WMTS_TILE_MATRIX_SET,
  format: CRACKMAP_WMTS_FORMAT,
  bands: [
    {
      layerId: 'wfs_layers:windsor-mfv-potholes-100mgrid-2026-27',
      style: POTHOLE_OVERVIEW_WMTS_STYLE,
      minZoom: 0,
      maxZoom: 13,
    },
    {
      layerId: 'wfs_layers:windsor-mfv-potholes-50mgrid-2026-27',
      style: POTHOLE_OVERVIEW_WMTS_STYLE,
      minZoom: 13,
      maxZoom: 15,
    },
    {
      layerId: 'wfs_layers:windsor-mfv-potholes-10mgrid-2026-27',
      style: POTHOLE_OVERVIEW_WMTS_STYLE,
      minZoom: 15,
      maxZoom: 17,
    },
    {
      layerId: 'wfs_layers:windsor-mfv-potholes-5mgrid-2026-27',
      style: POTHOLE_OVERVIEW_WMTS_STYLE,
      minZoom: 17,
      maxZoom: CRACKMAP_INITIAL_ZOOM,
    },
  ],
};

export function resolvePotholeOverviewWmts(
  sourceLayerId: string | null | undefined,
): DefectOverviewGridConfig | null {
  const trimmed = sourceLayerId?.trim();
  if (!trimmed) {
    return null;
  }
  if (trimmed === WINDSOR_POTHOLE_TYPE_NAME) {
    return WINDSOR_POTHOLE_OVERVIEW_WMTS;
  }
  return null;
}

export function isPotholeCatalogEnabled(): boolean {
  return process.env.REACT_APP_POTHOLE_WFS_ENABLED !== 'false';
}

export function getPotholeCatalogDefects(): readonly DefectCatalogItem[] {
  if (!isPotholeCatalogEnabled()) {
    return [];
  }

  return [
    {
      id: 'pothole-windsor-mfv',
      slug: toDefectSlug('windsor-mfv-potholes'),
      surveyType: 'mfv',
      name: 'Potholes',
      code: 'POTHOLE',
      type: 'Road Surface',
      presentation: 'pothole',
      measurements: [
        {
          name: 'Potholes',
          slug: toDefectMeasurementSlug('pothole-overlay'),
          code: 'POTHO',
          description:
            'Pothole polygons from GeoServer: grid overview when zoomed out; full WFS at street zoom (aveDepth: green 0–9.99, amber 10–19.99, red 20–250)',
          parameter: 'pothole',
          colorScheme: POTHOLE_RAG_COLOR_SCHEME,
          thresholds: [...POTHOLE_RAG_THRESHOLDS],
          surveyYears: [
            {
              id: 'windsor-2026-27',
              slug: toDefectSurveyYearSlug('windsor-2026-27'),
              surveyYear: '2026-27',
              startDate: '2026-04-01',
              endDate: '2027-03-31',
              sourceLayerId: WINDSOR_POTHOLE_TYPE_NAME,
              vectorSourceUrl: WINDSOR_POTHOLE_WFS_URL,
              lanes: ['all'],
            },
          ],
        },
      ],
    },
  ];
}
